import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { NutritionCalculationResult } from '../../lib/types';
import { db } from '../../lib/database';

interface DailyNutritionTargetsProps {
  nutritionTargets: NutritionCalculationResult | null;
  goal: string;
  isMobile?: boolean;
  animationDelay?: number;
  showIncompleteDataMessage?: boolean;
  missingFields?: string[];
}

export const DailyNutritionTargets = React.memo(function DailyNutritionTargets({ 
  nutritionTargets, 
  goal, 
  isMobile = false, 
  animationDelay = 0,
  showIncompleteDataMessage = false,
  missingFields = []
}: DailyNutritionTargetsProps) {
  const lastSavedTargets = useRef<string>('');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef<boolean>(false);
  const hasInitialized = useRef<boolean>(false);
  // Use singleton database service
  const [isMethodologyExpanded, setIsMethodologyExpanded] = useState(false);

  // Auto-save nutrition targets to database when component displays calculated values
  useEffect(() => {
    const saveNutritionTargets = async () => {
      if (!nutritionTargets || nutritionTargets.dailyCalories <= 0) return;
      
      // Prevent duplicate saves if one is already in progress
      if (isSavingRef.current) return;
      
      // Create a hash of the current targets to detect actual changes
      const currentTargets = {
        calories: Math.round(nutritionTargets.dailyCalories),
        protein: Math.round(nutritionTargets.macroTargets.protein.target),
        carbs: Math.round(nutritionTargets.macroTargets.carbs.target),
        fat: Math.round(nutritionTargets.macroTargets.fat.target),
        fiber: Math.round(nutritionTargets.macroTargets.fiber.target)
      };
      
      const targetsHash = JSON.stringify(currentTargets);
      
      // On first load, just store the hash without saving
      if (!hasInitialized.current) {
        // Initial load - storing nutrition targets hash
        lastSavedTargets.current = targetsHash;
        hasInitialized.current = true;
        return;
      }
      
      // Only save if targets have actually changed
      if (lastSavedTargets.current === targetsHash) {
        console.log('🚫 Skipping save - nutrition targets unchanged:', currentTargets);
        return;
      }
      
      try {
        isSavingRef.current = true; // Mark save as in progress
        console.log('💾 Auto-saving calculated nutrition targets to database:', currentTargets);
        
        const success = await db.updateCustomNutritionTargets(currentTargets);
        
        if (success) {
          console.log('✅ Nutrition targets saved successfully');
          lastSavedTargets.current = targetsHash;
        } else {
          console.error('❌ Failed to save nutrition targets');
        }
      } catch (error) {
        console.error('❌ Error saving nutrition targets:', error);
      } finally {
        isSavingRef.current = false; // Reset save flag
      }
    };

    // Clear any existing timeout to prevent multiple saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save operation to prevent multiple saves of the same values
    saveTimeoutRef.current = setTimeout(() => {
      saveNutritionTargets();
    }, 500); // 500ms debounce to handle toast-triggered re-renders

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [nutritionTargets]); // Only depend on the nutritionTargets object itself



  // Show helpful message when user data is incomplete
  if (showIncompleteDataMessage || !nutritionTargets) {
    const incompleteContent = (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/30 rounded-2xl p-4 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800/30 rounded-lg flex items-center justify-center">
            <span className="text-lg">📊</span>
          </div>
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Complete Your Profile for Nutrition Targets</h3>
        </div>
        
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">
            🎯 Personalized Targets Await!
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
            Fill out your body stats below to get accurate daily nutrition recommendations
          </p>
        </div>

        <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">
            📋 Required Information:
          </h4>
          <div className={`text-sm text-blue-700 dark:text-blue-300 ${isMobile ? 'space-y-1' : 'grid grid-cols-2 gap-2'}`}>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Age</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Gender</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Height</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Current Weight</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Goal (lose/maintain/gain)</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">•</span>
              <span>Activity Level</span>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800/30 rounded-lg p-3 mt-4">
          <div className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">
            ✨ What you'll get:
          </div>
          <div className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
            <div>• Personalized daily calorie target</div>
            <div>• Optimal protein, carb, and fat goals</div>
            <div>• Fiber and micronutrient recommendations</div>
            <div>• Diet-specific adjustments (keto, vegan, etc.)</div>
          </div>
        </div>
      </div>
    );

    // Wrap in motion.div for mobile animation
    if (isMobile) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: animationDelay }}
        >
          {incompleteContent}
        </motion.div>
      );
    }

    return incompleteContent;
  }

  // Show calculated nutrition targets when data is complete
  const content = (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/30 rounded-2xl p-4 mb-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-800/30 rounded-lg flex items-center justify-center">
          <span className="text-lg">🎯</span>
        </div>
        <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">Your Daily Nutrition Targets</h3>
      </div>
      
      {/* Daily Calories */}
      <div className="text-center mb-4">
        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
          {nutritionTargets.dailyCalories.toLocaleString()} calories
        </div>
        <p className="text-xs text-purple-700 dark:text-purple-300">
          Based on your stats, {goal} goal & {nutritionTargets.dietaryPattern} diet
        </p>
      </div>

      {/* Macronutrient Targets */}
      <div className={`grid gap-3 mb-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
        {/* Protein */}
        <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm">💪</span>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Protein</span>
          </div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {Math.round(nutritionTargets.macroTargets.protein.target)}g
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            {nutritionTargets.proteinPercentage}% of calories
          </div>
        </div>

        {/* Carbs */}
        <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm">🌾</span>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Carbs</span>
          </div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {Math.round(nutritionTargets.macroTargets.carbs.target)}g
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            {nutritionTargets.carbPercentage}% of calories
            {nutritionTargets.dietaryPattern === 'keto' && (
              <span className="block text-xs text-red-500 dark:text-red-400 font-medium">Keto limit!</span>
            )}
          </div>
        </div>

        {/* Fat */}
        <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm">🥑</span>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Fat</span>
          </div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {Math.round(nutritionTargets.macroTargets.fat.target)}g
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            {nutritionTargets.fatPercentage}% of calories
          </div>
        </div>

        {/* Fiber */}
        <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm">🥬</span>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Fiber</span>
          </div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {nutritionTargets.macroTargets.fiber.target}g
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            Daily minimum
          </div>
        </div>
      </div>

      {/* Collapsible Calculation Methodology & Special Considerations */}
      <div className={`${isMobile ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800/30 rounded-lg' : `transition-all duration-300 ease-in-out ${isMethodologyExpanded ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800/30 rounded-lg' : ''}`} mt-3`}>
        {/* Clickable Header */}
        {isMobile ? (
          // Mobile: Original simple design
          <button
            onClick={() => setIsMethodologyExpanded(!isMethodologyExpanded)}
            className="w-full p-3 text-left hover:bg-amber-100 dark:hover:bg-amber-800/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-amber-800 dark:text-amber-200">
                📋 {nutritionTargets.dietaryPattern === 'standard' ? 'Balanced Diet' : nutritionTargets.dietaryPattern.charAt(0).toUpperCase() + nutritionTargets.dietaryPattern.slice(1)} Calculation Method
              </div>
              <div className="text-amber-600 dark:text-amber-400 ml-2">
                <svg 
                  className={`w-4 h-4 transform transition-transform duration-200 ${isMethodologyExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {!isMethodologyExpanded && (
              <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Click to view calculation details and important disclaimer
              </div>
            )}
          </button>
        ) : (
          // Desktop: Enhanced design with centered bar
          <div className="flex justify-center">
            <button
              onClick={() => setIsMethodologyExpanded(!isMethodologyExpanded)}
              className={`transition-all duration-300 ease-in-out text-left bg-amber-100/60 dark:bg-amber-800/25 hover:bg-amber-100 dark:hover:bg-amber-800/35 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50 border border-amber-200/60 dark:border-amber-700/40 cursor-pointer group ${
                isMethodologyExpanded 
                  ? 'w-full p-3' 
                  : 'w-4/5 py-2 px-4'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-xs">📋</span>
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-200 group-hover:text-amber-900 dark:group-hover:text-amber-100 transition-colors">
                    {nutritionTargets.dietaryPattern === 'standard' ? 'Balanced Diet' : nutritionTargets.dietaryPattern.charAt(0).toUpperCase() + nutritionTargets.dietaryPattern.slice(1)} Calculation Method
                  </div>
                </div>
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors mr-1">
                  <span className="text-xs font-medium">
                    {isMethodologyExpanded ? 'Hide' : 'Show'}
                  </span>
                  <svg 
                    className={`w-4 h-4 transform transition-transform duration-200 ${isMethodologyExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {!isMethodologyExpanded && (
                <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors text-left">
                  Click to view calculation details and important disclaimer
                </div>
              )}
            </button>
          </div>
        )}

        {/* Expandable Content */}
        {isMethodologyExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="px-3 pb-3"
          >
            {/* Calculation Sources */}
            <div className={`text-xs text-amber-700 dark:text-amber-300 mb-2 ${isMobile ? 'space-y-0.5' : 'space-y-1'}`}>
              {isMobile ? (
                <>
                  <div className="mt-3"><strong>• Calories:</strong> Mifflin-St Jeor + WHO activity factors</div>
                  <div><strong>• Protein:</strong> WHO + PDCAAS bioavailability for {nutritionTargets.dietaryPattern}</div>
                  <div><strong>• Macros:</strong> Evidence-based ratios from clinical research</div>
                </>
              ) : (
                <>
                  <div className="mt-3"><strong>• Calories:</strong> Mifflin-St Jeor equation + WHO activity factors + goal adjustment</div>
                  <div><strong>• Protein:</strong> WHO guidelines + PDCAAS bioavailability scores for {nutritionTargets.dietaryPattern} diet</div>
                  <div><strong>• Carbs & Fat:</strong> Evidence-based ratios from clinical research and dietary pattern studies</div>
                  <div><strong>• Fiber:</strong> RDA recommendations adjusted for dietary pattern</div>
                </>
              )}
            </div>

            {/* Special Considerations */}
            {nutritionTargets.specialConsiderations && (
              <div className={isMobile ? 'space-y-0.5' : 'space-y-1'}>
                <div className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                  {isMobile ? 'Special Notes:' : 'Special Considerations:'}
                </div>
                {nutritionTargets.specialConsiderations.requiresB12Supplementation && (
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    • B12 supplementation critical for plant-based{isMobile ? '' : ' diets'}
                  </div>
                )}
                {nutritionTargets.specialConsiderations.requiresOmega3Supplementation && (
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    • Omega-3 supplementation recommended{isMobile ? '' : ' (algae-based for vegan)'}
                  </div>
                )}
                {nutritionTargets.specialConsiderations.ironAbsorptionNote && (
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    • {nutritionTargets.specialConsiderations.ironAbsorptionNote}
                  </div>
                )}
                {nutritionTargets.bioavailabilityFactor < 0.9 && (
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    • Protein {isMobile ? '+' : 'increased '}
                    {Math.round((1/nutritionTargets.bioavailabilityFactor - 1) * 100)}% for 
                    {isMobile ? ' amino completeness' : ' optimal amino acid completeness'}
                    {!isMobile && ` (PDCAAS factor: ${(nutritionTargets.bioavailabilityFactor * 100).toFixed(0)}%)`}
                  </div>
                )}
              </div>
            )}

            {/* Professional Disclaimer */}
            <div className="border-t border-amber-200 dark:border-amber-800/50 pt-2 mt-2">
              <div className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                ⚠️ Important Disclaimer:
              </div>
              <div className="text-xs text-amber-700 dark:text-amber-300">
                {isMobile ? (
                  "These targets are evidence-based guidelines for general wellness, not medical advice. Individual needs may vary. Consult healthcare professionals for personalized nutrition plans."
                ) : (
                  "These nutrition targets are evidence-based guidelines calculated using established scientific formulas and dietary research. They are intended for general wellness guidance and should not be considered strict medical requirements. Individual nutritional needs may vary based on health conditions, medications, metabolism, and other factors. Please consult qualified healthcare professionals or registered dietitians for personalized nutrition advice, especially if you have specific health concerns or medical conditions."
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );

  // Wrap in motion.div only for mobile (since desktop doesn't use animation)
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
      >
        {content}
      </motion.div>
    );
  }

  return content;
}); 